import { useFragment } from "@/generated";
import { ProjectFieldsFragmentDoc, RestoreProjectsDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for restoring projects from trash.
 *
 * Provides a mutation function to restore one or more trashed projects back to active state.
 * Automatically refetches relevant queries to update the UI.
 *
 * @returns An object containing:
 *   - restoreProjects: Function to restore one or more projects (accepts single ID or array of IDs)
 *   - loading: Boolean indicating if the mutation is in progress
 *   - error: ApolloError if the mutation failed, undefined otherwise
 *
 * @example
 * const { restoreProjects, loading, error } = useRestoreProject();
 * // Restore single project
 * await restoreProjects("project123");
 * // Restore multiple projects
 * await restoreProjects(["project1", "project2", "project3"]);
 */
export const useRestoreProjects = () => {
  const [restoreProjectsMutation, { loading, error }] = useMutation(RestoreProjectsDocument, {
    update(cache, { data }) {
      if (!data?.restoreProjects) return;

      try {
        const projects = data.restoreProjects.map((project) =>
          useFragment(ProjectFieldsFragmentDoc, project)
        );

        const projectsByParentIdMap = new Map<string | null, typeof projects>();

        for (const project of projects) {
          const key = project.parentId ?? null;

          const projectsByParent = projectsByParentIdMap.get(key) ?? [];
          projectsByParent.push(project);
          projectsByParentIdMap.set(key, projectsByParent);
        }

        cache.batch({
          update(cache) {
            for (const project of projects) {
              const id = cache.identify(project);
              if (id) {
                cache.writeFragment({
                  id,
                  fragment: ProjectFieldsFragmentDoc,
                  data: project,
                });
              }

              // Update project isTrash => false
              cache.modify({
                id: `Project:${project.id}`,
                fields: {
                  isTrash() {
                    return false;
                  },
                },
              });
            }

            for (const [parentId, projects] of projectsByParentIdMap.entries()) {
              const newRefs = projects
                .map((p) => cache.identify(p))
                .filter(Boolean)
                .map((id) => ({ __ref: id }));

              // Restore project to main page
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  projectsByParentId(existingData, { storeFieldName }) {
                    const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                    const isTrashKey = '"isTrash":false';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;
                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: [...newRefs, ...existingData.items],
                      total: (existingData.total || 0) + newRefs.length,
                    };
                  },
                },
              });

              // Increment parent itemCount , Decrement trashCount
              if (parentId !== null) {
                cache.modify({
                  id: `Project:${parentId}`,
                  fields: {
                    itemCount(existingData = 0) {
                      return existingData + newRefs.length;
                    },

                    trashCount(existingData = 0) {
                      return existingData - newRefs.length;
                    },
                  },
                });
              }

              // Remove project from trash page
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  projectsByParentId(existingData, { storeFieldName, readField }) {
                    const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                    const isTrashKey = '"isTrash":true';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;
                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: existingData.items.filter(
                        (ref: Reference) => !projects.some((p) => readField("id", ref) === p.id)
                      ),
                      total: (existingData.total || 0) - newRefs.length,
                    };
                  },
                },
              });

              // Decrement Trash count number
              if (parentId === null) {
                cache.modify({
                  id: "ROOT_QUERY",
                  fields: {
                    rootTrashCount(existingData) {
                      return existingData - newRefs.length;
                    },
                  },
                });
              }
            }
          },
        });
      } catch (error) {
        console.error("Restore project cache update failed:", error);
      }
    },
  });

  const restoreProjects = async (ids: string[]) => {
    return withToast(
      async () => {
        const result = await restoreProjectsMutation({ variables: { ids: ids } });

        const maskedProjects = result.data?.restoreProjects ?? [];
        const restoredProjects = maskedProjects.map((project) =>
          useFragment(ProjectFieldsFragmentDoc, project)
        );

        return restoredProjects;
      },
      {
        loading: `Restoring ${ids.length} project${ids.length !== 1 ? "s" : ""}...`,
        success: `Successfully restored ${ids.length} project${ids.length !== 1 ? "s" : ""}`,
        error: "Failed to restore projects. Please try again.",
      },
      "restoreProjects"
    );
  };

  return {
    restoreProjects,
    loading,
    error,
  };
};
